import { supabase } from "@/integrations/supabase/client";

export type ReportReason = "harassment" | "spam" | "inappropriate" | "other";
export type ContentType =
  | "user"
  | "review"
  | "collection"
  | "list_item"
  | "discussion"
  | "discussion_reply"
  | "message"
  | "username"
  | "bio";

export const REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: "harassment", label: "Harassment or hate speech" },
  { value: "spam", label: "Spam or misleading" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "other", label: "Other" },
];

export interface SubmitReportInput {
  reportedUserId?: string | null;
  contentType: ContentType;
  contentId?: string | null;
  reason: ReportReason;
  note?: string;
}

export async function submitReport(input: SubmitReportInput) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("You must be signed in to report.");
  const { error } = await (supabase.from("reports") as any).insert({
    reporter_id: user.id,
    reported_user_id: input.reportedUserId ?? null,
    content_type: input.contentType,
    content_id: input.contentId ?? null,
    reason: input.reason,
    note: input.note?.trim() || null,
  });
  if (error) throw error;
}

export async function blockUser(targetId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Sign in required.");
  if (user.id === targetId) throw new Error("You can't block yourself.");
  const { error } = await (supabase.from("user_blocks") as any).insert({
    blocker_id: user.id,
    blocked_id: targetId,
  });
  if (error && !`${error.message}`.includes("duplicate")) throw error;
}

export async function unblockUser(targetId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Sign in required.");
  const { error } = await supabase
    .from("user_blocks")
    .delete()
    .eq("blocker_id", user.id)
    .eq("blocked_id", targetId);
  if (error) throw error;
}

export async function isBlocked(targetId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const { data } = await (supabase.from("user_blocks") as any)
    .select("id")
    .eq("blocker_id", user.id)
    .eq("blocked_id", targetId)
    .maybeSingle();
  return !!data;
}

export async function checkSelfBanned(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const { data } = await (supabase.from("banned_users") as any)
    .select("user_id")
    .eq("user_id", user.id)
    .maybeSingle();
  return !!data;
}
